import { Checkbox, FormControlLabel, Grid, TextField, Typography } from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { useField, useFormikContext } from 'formik';
import React from 'react';

interface FormikWorkingDayProps {
    dayName: string;
    namePrefix: string;
    readOnly?: boolean;
}

const FormikWorkingDay: React.FC<FormikWorkingDayProps> = ({ dayName, namePrefix, readOnly = false }) => {
    const startsAtFieldName = `${namePrefix}.StartsAt`;
    const endsAtFieldName = `${namePrefix}.EndsAt`;

    const [startsAtField, startsAtMeta] = useField(startsAtFieldName);
    const [endsAtField, endsAtMeta] = useField(endsAtFieldName);
    const { setFieldValue } = useFormikContext();

    const isNotWorking = !startsAtField.value && !endsAtField.value;

    const handleNotWorkingChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            setFieldValue(startsAtFieldName, '');
            setFieldValue(endsAtFieldName, '');
        } else {
            setFieldValue(startsAtFieldName, '09:00');
            setFieldValue(endsAtFieldName, '17:00');
        }
    };

    const convertTimeStringToDate = (timeStr: string): Date | null => {
        if (!timeStr) return null;
        const [hours, minutes] = timeStr.split(':').map(Number);
        const date = new Date();
        date.setHours(hours, minutes, 0, 0);
        return date;
    };

    const convertDateToTimeString = (date: Date | null): string => {
        if (!date) return '';
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${hours}:${minutes}`;
    };

    const handleStartTimeChange = (newValue: unknown) => {
        setFieldValue(startsAtFieldName, convertDateToTimeString(newValue as Date | null));
    };

    const handleEndTimeChange = (newValue: unknown) => {
        setFieldValue(endsAtFieldName, convertDateToTimeString(newValue as Date | null));
    };

    return (
        <Grid container spacing={1} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                    {dayName}
                </Typography>
            </Grid>
            <Grid size={{ xs: 12 }}>
                <FormControlLabel control={<Checkbox checked={isNotWorking} onChange={handleNotWorkingChange} disabled={readOnly} />} label="Not Schedulable" />
            </Grid>
            {!isNotWorking && (
                <>
                    <Grid size={{ xs: 12 }}>
                        <TimePicker
                            label="Starts At"
                            value={convertTimeStringToDate(startsAtField.value)}
                            onChange={handleStartTimeChange}
                            disabled={readOnly}
                            minutesStep={5}
                            ampm={true}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    error: Boolean(startsAtMeta.error),
                                    helperText: startsAtMeta.error,
                                },
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <TimePicker
                            label="Ends At"
                            value={convertTimeStringToDate(endsAtField.value)}
                            onChange={handleEndTimeChange}
                            disabled={readOnly}
                            minutesStep={5}
                            ampm={true}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    error: Boolean(endsAtMeta.error),
                                    helperText: endsAtMeta.error,
                                },
                            }}
                        />
                    </Grid>
                </>
            )}
        </Grid>
    );
};

export default FormikWorkingDay;
